import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Checkbox, CheckboxProps, notification } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useNavigate } from 'react-router-dom';
import { uiModel } from 'entities/ui';
import { FrequencyDates, FrequencyDatesToBack, NewsTabs, WeekDays } from 'shared/enums/news';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { useCreateNewsMutation } from 'app/services/edpApiV1/news';
import { TAddUserEmployee } from 'shared/types/news.types';
import { TDivisionResponse, TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import { prepareDateTimeToSave } from 'shared/helpers/utils';
import { currentTimeToBack, getClosestBiggerTime } from 'shared/helpers/date';
import { fixContentEditor } from 'shared/helpers/text-editor';
import dayjs from 'dayjs';
import { PICKER_TIME_MASK } from 'shared/constants';
import { MINUTE_STEP } from 'shared/constants/date';
import ModalCancelSubmit from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import DateTime from '../DateTime/DateTime';
import Frequency from '../Frequency/Frequency';
import NewsEditor from '../../../shared/components/text-editor/TextEditor';
import PickUsers from '../pickUsers/PickUsers';
import Title from '../../../shared/components/title/Title';
import style from './index.module.scss';

const breadCrumbs = [
  { name: 'Новости', link: '/news' },
  { name: 'Добавить новость', link: `/news/create` },
];

const CreateNews = () => {
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const me = useAppSelector(state => state.entities.viewer.me);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>('');
  const [retry, setRetry] = useState<FrequencyDates>(FrequencyDates.NONE);
  const [text, setText] = useState<string>('');
  const [users, setUsers] = useState<TAddUserEmployee[]>([]);
  const [divisions, setDivisions] = useState<TDivisionResponse[]>([]);
  const [organizations, setOrganizations] = useState<TOrganizationResponse[]>([]);
  const [datesToCreate, setDatesToCreate] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>();
  const [time, setTime] = useState<string | null>(getClosestBiggerTime());
  const [days, setDays] = useState<WeekDays[]>([]);

  const [createNewsQuery, { isLoading: isCreateNewsLoadig }] = useCreateNewsMutation();
  const possibleMinutes = useRef<number[]>([]);
  const onTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTitle(event.target.value);
    },
    [dispatch],
  );

  const onDefferedPublicationToggled: CheckboxProps['onChange'] = e => {
    if (e.target.checked === false) {
      setRetry(FrequencyDates.NONE);
    } else {
      setRetry(FrequencyDates.ONCE);
    }
  };

  const validateNews = (): boolean => {
    return !title || !text;
  };

  const prepareNewsToSave = useCallback(() => {
    const { utcDate, utcTime, utcDatesToCreate, utcDays } = prepareDateTimeToSave({
      datesToCreate,
      days,
      retry,
      startDate,
      time,
    });
    const isNotDeferred = retry !== FrequencyDates.NONE;
    return {
      created: currentTimeToBack(),
      title: title,
      content: fixContentEditor(text),
      userIds: users.map(user => user.id),
      divisionIds: divisions.map(user => user.id),
      organizationIds: organizations.map(user => user.id),
      retry: FrequencyDatesToBack[retry],
      datesToCreate: isNotDeferred ? utcDatesToCreate : [],
      startDate: isNotDeferred ? utcDate : undefined,
      time: isNotDeferred ? utcTime : undefined,
      days: isNotDeferred ? utcDays : undefined,
    };
  }, [datesToCreate, days, divisions, organizations, retry, startDate, text, time, title, users]);

  useEffect(() => {
    let currentMinute = 0;
    while (currentMinute < 60) {
      possibleMinutes.current.push(currentMinute);
      currentMinute += MINUTE_STEP;
    }
  }, []);

  useEffect(() => {
    const date = dayjs(time, PICKER_TIME_MASK);
    const minute = date.minute();

    if (!possibleMinutes.current.includes(minute) && time) {
      const candidate = { diff: Infinity, value: 0 };
      possibleMinutes.current.forEach(elem => {
        const newDiff = Math.abs(minute - elem);
        if (newDiff < candidate.diff) {
          candidate.diff = newDiff;
          candidate.value = elem;
        }
      });
      const newDate = date.set('minute', candidate.value);
      const formattedDateString = newDate.format(PICKER_TIME_MASK);
      setTime(formattedDateString);
      return;
    }
  }, [time]);

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    navigate('/news');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    setOpenCanelModal(true);
  };

  const createNews = async () => {
    await createNewsQuery(prepareNewsToSave())
      .then(data => {
        if (Object.keys(data).includes('error')) {
          notification.error({
            message: returnErrorOrMessage(data, 'Не удалось создать новость'),
          });
        } else {
          notification.success({
            message: 'Новость создана успешно',
          });
          dispatch(uiModel.Tabs.actions.setActive(NewsTabs.UNPUBLICATED));
          navigate('/news');
        }
      })
      .catch((e: any) => {
        notification.error({
          message: returnErrorOrMessage(e, 'Не удалось создать новость'),
        });
      });
  };

  const onEditorValueChange = (val: string) => {
    setText(val);
  };

  const changeUsers = (usersToChange: TAddUserEmployee[]) => {
    setUsers(usersToChange);
  };

  const changeDivisions = (divisionsToChange: TDivisionResponse[]) => {
    setDivisions(divisionsToChange);
  };

  const changeOrganizations = (organizationsToChange: TOrganizationResponse[]) => {
    setOrganizations(organizationsToChange);
  };

  const onFrequencyChange = (data: FrequencyDates) => {
    setRetry(data);
  };

  const onDateChange = (dates: string[] | string, isStartDate: boolean) => {
    if (isStartDate) {
      setStartDate(dates ? (dates as string) : undefined);
    } else {
      setDatesToCreate(dates as string[]);
    }
  };

  const onTimeChange = (time: string | null) => {
    setTime(time);
  };

  const onDaysChange = (days: WeekDays[]) => {
    setDays(days);
  };

  return (
    <div className={style.wrap}>
      <div className={style['top-block']}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <Button onClick={cancelModalOpen}>Отмена</Button>
        <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
      </div>
      <Title onTitleChange={onTitleChange} />
      <NewsEditor onValueChange={onEditorValueChange} title="Текст новости" />
      <PickUsers
        changeUsers={changeUsers}
        selectedUsers={users}
        selectedDivisions={divisions}
        addDivisions={changeDivisions}
        addOrganizations={changeOrganizations}
        selectedOrganizations={organizations}
      />
      <div className={style['deferred-block']}>
        <div className={style.deferred}>
          <Checkbox onChange={onDefferedPublicationToggled}>Отложенная публикация</Checkbox>
        </div>
        {retry !== FrequencyDates.NONE ? (
          <div className={style.date}>
            <Frequency retry={retry} onChange={onFrequencyChange} />
            <DateTime
              retry={retry}
              datesToCreate={datesToCreate}
              startDate={startDate}
              time={time as string}
              days={days}
              onDateChange={onDateChange}
              onTimeChange={onTimeChange}
              onDaysChange={onDaysChange}
            />
          </div>
        ) : null}
      </div>
      <div className={style['create-btn']}>
        <Button type="primary" onClick={createNews} disabled={validateNews()} loading={isCreateNewsLoadig}>
          Создать
        </Button>
      </div>
    </div>
  );
};

export default CreateNews;
